import { mediaService } from '@/services/media.service';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MDocumentCard from '@/components/ui/atoms/MDocumentCard';
import { IconInfoCircle, IconEdit, IconMessage2 } from '@tabler/icons-react';
import { MButton } from '@/components/ui/atoms/MButtons';
import DocumentInfoDrawer from './DocumentInfoDrawer';
import DocumentDrawer from './DocumentDrawer';
import DocumentCommentDrawer from './DocumentCommentDrawer';
import { useDocument } from '@/hooks/useDocument';
import { getMemberInfo } from '@/hooks/useMembers';
import styles from './document.module.css';
import PDFPreview from './PDFPreview';

const DocumentViewer: React.FC = () => {
  const [infoDrawerOpen, setInfoDrawerOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const initialDocument = location.state?.document;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = React.useState(false);
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [doc, setDoc] = React.useState(initialDocument);

  // Scroll to top when component mounts
  React.useEffect(() => {
    // Most reliable cross-browser approach - target all possible scroll containers
    const scrollToTop = () => {
      // Try all possible scroll containers
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: 0, behavior: 'instant' });
      document.body.scrollTo({ top: 0, behavior: 'instant' });

      // Also find any overflow:auto/scroll divs and reset them
      document.querySelectorAll('[style*="overflow: auto"], [style*="overflow: scroll"], [class*="overflow"]').forEach(el => {
        if (el instanceof HTMLElement) {
          el.scrollTop = 0;
        }
      });

      // Common scroll container selectors
      document.querySelectorAll('div[style*="overflow-y: auto"], div[style*="overflow-y: scroll"], .overflow-y-auto, .overflow-y-scroll').forEach(el => {
        if (el instanceof HTMLElement) {
          el.scrollTop = 0;
        }
      });
    };

    // Run immediately and again after a short delay to ensure DOM is ready
    scrollToTop();
    setTimeout(scrollToTop, 50);
  }, []);

  // Format date as "2nd Mar 2026"
  const formatDocumentDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      
      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinalSuffix = (n: number) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
      };
      
      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch {
      return dateString;
    }
  };

  // Defensive fallback
  if (!doc) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p>Document not found.</p>
        <button onClick={() => navigate('/documents/list')}>Back to List</button>
      </div>
    );
  }

  // Get memberId for API
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || '';
  const { updateDocumentFlag, downloadDocument, deleteDocument, updateDocumentNotes } = useDocument(memberId);

  // Handlers
  const handleFlagChange = async (flag: 'isImportant' | 'isPrivate' | 'availableOffline', value: boolean) => {
    await updateDocumentFlag(doc, flag, value);
    setDoc((prev: any) => ({ ...prev, [flag]: value }));
  };
  const handleDownload = async () => {
    await downloadDocument(doc.media?.fileKey, doc.media?.fileName);
  };
  const handleDelete = async () => {
    await deleteDocument(doc);
    navigate('/documents/list');
  };
  const handleCommentClick = () => {
    setCommentDrawerOpen(true);
  };
  const handleCommentDone = async (value: string) => {
    setCommentLoading(true);
    try {
      await updateDocumentNotes(doc, value);
      setDoc((prev: any) => ({ ...prev, notes: value }));
    } finally {
      setCommentLoading(false);
      setCommentDrawerOpen(false);
    }
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen py-8">
      {/* Document Card at the top */}
      <div className={styles.documentViewCard}>
        <div className="relative">
  <MDocumentCard
    cardType="document"
    category={doc.category}
    key={doc.id}
    displayName={doc.displayName || 'Document'}
    insights={doc.insights || false}
    hideActions={true}
    meta={
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Dated {doc.documentDate ? formatDocumentDate(doc.documentDate) : ''}</span>
        <button
          aria-label="Document Info"
          onClick={(e) => {
            e.stopPropagation();
            setInfoDrawerOpen(true);
          }}
          type="button"
          style={{ display: 'flex', alignItems: 'center', background: '#EDF3FC', border: 'none', cursor: 'pointer', padding: 0, borderRadius: '24px' }}
        >
          <IconInfoCircle size={20} stroke={1.6} style={{ color: '#3263A3' }} />
        </button>
      </div>
    }
    viewer
  />
</div>
      </div>
      {/* Document Drawer */}
      <DocumentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        displayName={doc.displayName || 'Document'}
        fileKey={doc.media?.fileKey}
        fileName={doc.media?.fileName || doc.displayName || 'Document'}
        isImportant={!!doc.isImportant}
        isPrivate={!!doc.isPrivate}
        availableOffline={!!doc.availableOffline}
        onImportantChange={checked => handleFlagChange('isImportant', checked)}
        onPrivateChange={checked => handleFlagChange('isPrivate', checked)}
        onOfflineChange={checked => handleFlagChange('availableOffline', checked)}
        onDownloadClick={handleDownload}
        onDelete={handleDelete}
        onCommentClick={handleCommentClick}
        commentButtonText={doc.notes ? 'Edit Comment' : 'Add Comment'}
      />
      {/* Info Drawer */}
<DocumentInfoDrawer
  open={infoDrawerOpen}
  onClose={() => setInfoDrawerOpen(false)}
  fileName={doc.displayName || 'Document'}
  size={doc.media?.size}
  uploadedAt={doc.media?.createdAt || doc.createdAt}
/>
{/* Comment Drawer */}
      <DocumentCommentDrawer
        open={commentDrawerOpen}
        onClose={() => setCommentDrawerOpen(false)}
        initialValue={doc.notes || ''}
        onDone={handleCommentDone}
        loading={commentLoading}
        title="Edit Comment"
      />
      {/* Main Preview */}
      <div className="w-full max-w-2xl mx-auto">
        <DocumentPreviewInline
          memberId={memberId}
          fileKey={doc.media?.fileKey}
          mimeType={doc.media?.mimeType}
          fileName={doc.media?.fileName}
        />
      </div>
      {/* Comment section */}
      <div className="confirm-footer">
        {doc.notes ? (
          <div className={styles.commentSection}>
            <div className={styles.commentHeader}>
              <span className='body-base-semibold default-text-color'>Comment</span>
              <button
                className={styles.commentEditBtn}
                aria-label={doc.notes ? 'Edit Comment' : 'Add Comment'}
                onClick={() => setCommentDrawerOpen(true)}
                type="button"
              >
                <IconEdit size={20} stroke={1.6} color="#45168C" />
              </button>
            </div>
            <div className="body-xs-medium">{doc.notes}</div>
          </div>
        ) : (
          <MButton
            variant="outline"
            size="md"
            leftIcon={<IconMessage2 size={16} style={{ color: 'var(--brand-colours-indigo-indigo-400, #7046AE)' }} />}
            style={{ width: '100%', borderRadius: 20, fontWeight: 500, color: 'var(--brand-colours-indigo-indigo-400, #7046AE)', fontFamily: 'var(--typography-font-family-base, Manrope)', fontSize: 'var(--typography-font-size-body-s, 16px)' }}
            onClick={() => setCommentDrawerOpen(true)}
          >
            Add comment
          </MButton>
        )}
      </div>
    </div>
   
  );
};

// Inline DocumentPreview logic for blob download and preview
const DocumentPreviewInline: React.FC<{ memberId: string; fileKey?: string; mimeType?: string; fileName?: string; className?: string }> = ({ memberId, fileKey, mimeType, fileName = '', className = '' }) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!fileKey) return;
    setPreviewUrl(null);
    setPreviewError(null);
    let url: string | null = null;
    const fetchPreview = async () => {
      try {
        const blob = await mediaService.download(memberId, fileKey);
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (e) {
        setPreviewError('Failed to load preview.');
      }
    };
    fetchPreview();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileKey]);

  if (!fileKey) return <span className="text-gray-400 text-base">No file to preview</span>;

  return (
    <>
    {/* Override the viewport for this page only */}
    <div className={`flex flex-col items-center justify-center min-h-[320px] ${className}`}>
      {!previewUrl && !previewError && (
        <span className="text-gray-400 text-base">Loading preview...</span>
      )}
      {previewError && (
        <span className="text-red-500 text-base">{previewError}</span>
      )}
      {previewUrl && mimeType?.startsWith('image') && (
        <img
          src={previewUrl}
          alt={fileName}
          className="max-h-[67vh] w-full h-auto shadow object-contain"
          style={{ objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      )}
      {previewUrl && mimeType === 'application/pdf' && (
        <PDFPreview fileUrl={previewUrl} />
      )}
      {previewUrl && mimeType?.startsWith('video') && (
        <video controls src={previewUrl} className="w-full max-h-[65vh] shadow" />
      )}
      {/* Fallback for other types */}
      {previewUrl && !mimeType?.startsWith('image') && mimeType !== 'application/pdf' && !mimeType?.startsWith('video') && (
        <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-lg">
          Open file
        </a>
      )}
    </div>
    </>
  );
};

export default DocumentViewer;
